/**
 * « Générer une activité » du tuteur hors live — `tutor/students/GenerateActivityModal.js` dans ses quatre
 * entrées de l'espace tuteur (`tutorStudentTabs.ts`, `TutorGenerateTarget`) :
 *  - « Activité » de Mes élèves (`forStudents`) : une activité commune, le choix des élèves ouvert d'emblée,
 *    tous cochés (`selectedStudentIds` = `studentsList`), « Confirmer (N) » ;
 *  - « Activité » de la fiche, le « + » de la liste d'activités : un élève ;
 *  - « Créer une activité » d'une pièce de la conversation (`initialImage`) : l'étape du fichier d'emblée,
 *    après le recadrage au web pour une image ;
 *  - « Générer... » de la fiche d'une activité (`ParcoursChoiceModal.js`) : Type, Durée, Instructions, puis
 *    les trois semblables de l'exo (`mode="similar"`) ; quand son chapitre n'est pas fiable, il se choisit sur
 *    place d'abord (`useSourceChapterFix`, la même correction que l'ancienne fenêtre et le fil de la salle).
 *
 * Le moteur est le parcours partagé (`useGenerateActivityFlow`, le réducteur porté de la même modale) : ses
 * appels, ses corps et leur ordre, à brouillon égal. Ce crochet n'ajoute que ce que le tuteur a de plus,
 * chaque fois lu dans le legacy :
 *  - le formulaire du tuteur : l'en-tête « Classe · Matière » hors programme, un chapitre OU des
 *    instructions (`isGenerationFormValid`), la recherche sans résultat qui bascule vers Instructions ;
 *  - ses contrôles à venir pour un élève (`getSchoolAssessments`, `upcoming`, à l'ouverture) : les chapitres
 *    du prochain en tête ;
 *  - le mode programme de révision (`useStudyProgram`) : allumé à l'ouverture pour un élève qui a un
 *    programme, ou dès qu'un élève choisi en a un ; le chapitre se choisit dans ses programmes
 *    (`getStudyProgramsAsTutor`, `student_ids`) et porte la classe et la matière ;
 *  - le quiz de prérequis, offert par la salle de la première séance du tuteur (`prerequisite-quiz`), allumé
 *    d'office sur l'Entraînement et la Révision ; un échec de création avec le quiz se dit sur sa carte ;
 *  - après une création depuis un fichier, pour un élève, sa relecture (`getStudent`) : le chat montre la
 *    pastille de l'activité née de la pièce.
 * Hors live, aucun message de salle (`newTodo`) ne part : le legacy ne l'envoie qu'en live.
 *
 * Au web, une photo (déposée, choisie ou venue de la conversation) passe d'abord par « Recadrer l'image »
 * (`useTutorImageCrop`, le même crochet que la salle du live).
 *
 * Retiré : « Ancienne interface » (`new-todo`), qu'aucun espace porté n'ouvre plus.
 *
 * Une seule surface à la fois, comme la modale et ses sous-modales : le recadrage, le choix d'un chapitre du
 * programme, ou le parcours (le choix des élèves se pose à sa place, `<TutorGenerateActivityFlow picker>`).
 */
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react"
import { useDispatch, useSelector, useStore } from "react-redux"

import { apiRequest } from "../../../../../../data/redux/actions/api"
import { schoolAssessmentsService } from "../../../../../../data/services/schoolAssessmentsService"
import { studentsService } from "../../../../../../data/services/studentsService"
import { TutorGenerateActivityFlow } from "../../../../../../design-system/tutor"
import { TutorImageCropStep } from "../../../../../../design-system/templates/TutorImageCropStep"
import { TutorRevisionProgramSheet } from "../../../../../../design-system/templates/TutorRevisionProgramSheet"
import { type TutorGenerateActivityFlowProps } from "../../../../../../design-system/templates/TutorGenerateActivityFlow"
import {
    DEFAULT_SCHOOL_SUBJECT_ID,
    defaultMaterialDuration,
    lastFinishedActivitySubjectId,
} from "../../../../adapters/parent/Activities/activityGenerationAdapter"
import {
    describeInInstructions,
    missingStudentGenerationField,
    nextAssessmentForSubject,
    type ApiStudentSchoolAssessment,
} from "../../../../adapters/student/Activities/studentGenerationAdapter"
import {
    anyHasStudyProgram,
    fromActivityTitle,
    generationSourceOf,
    prerequisiteQuizAvailable,
    programChapterScope,
    quizOnByDefault,
    toGenerateTargetStudents,
    TUTOR_GENERATE_FORM_FIELDS,
    TUTOR_GENERATE_FROM_ACTIVITY_FIELDS,
    TUTOR_GENERATE_FROM_ACTIVITY_FIX_FIELDS,
    TUTOR_GENERATE_PROGRAM_FIELDS,
    type ApiGenerateProgram,
    type ApiGenerateStudent,
    type TutorGenerateSource,
} from "../../../../adapters/tutor/Students/tutorGenerateActivityAdapter"
import {
    selectAccount,
    selectAccountStudents,
    selectDisplayedStudent,
} from "../../../../view-models/shared/displayedStudent"
import {
    SOURCE_CHAPTER_FIX_TEXTS,
    sourceChapterFixNotice,
} from "../../../../view-models/tutor/Students/sourceChapterFix"
import {
    commonTargetClass,
    emptyGenerationDraft,
    useGenerateActivityFlow,
} from "../../../hooks/useGenerateActivityFlow"
import { type UIActivityFile, type UIActivityGenerationDraft } from "../../../types"
import { type UITutorProgramChapter } from "../../../types/tutorEleves"
import { useTutorImageCrop } from "../../../hooks/useTutorImageCrop"
import { type SourceChapterFix, useSourceChapterFix } from "../../../hooks/useSourceChapterFix"
import { useTutorProgramPicker } from "../Lives/useTutorProgramPicker"
import { type OpenGeneration, type TutorGenerateTarget, type TutorStudentId } from "./tutorStudentTabs"

/**
 * Ce que chaque geste envoie, comme `GenerateActivityModal.js` hors live — tenu par un test par ligne
 * (`useTutorGenerateActivity.equivalence.hook.test.tsx`). Les appels propres au parcours partagé (lecture d'un
 * fichier, similaires d'un fichier, repli inter-classes) sont tenus par ses propres tests.
 */
export const TUTOR_GENERATE_ACTIVITY_EQUIVALENCE = {
    students_open: {
        gesture: "« Activité » de Mes élèves : le choix ouvert d'emblée, tous cochés ; les chapitres de tous",
        legacy: "MyStudentsTabScreen.js:156-166 ; GenerateActivityModal.js:148-149, 762-798",
        calls: [
            {
                service: "getChaptersAsTutor",
                routeParams: "{ id: undefined }",
                urlParams: "{ school_subject_id, class, page_size: 100, student_ids: « 11,12 » }",
            },
        ],
    },
    students_generate: {
        gesture: "Activité commune : les trois énoncés, l'historique de tous les élèves",
        legacy: "GenerateActivityModal.js:1039-1124",
        calls: [
            {
                service: "generateMaterialsSkillsPart1-3",
                routeParams: "{ id: premier élève choisi }",
                data: "{ class, school_subject_id, instructions, chapter_id, level, duration, student_ids, request_batch_id }",
            },
        ],
    },
    students_create: {
        gesture: "Activité commune : « Créer l'activité » d'une carte",
        legacy: "GenerateActivityModal.js:1435-1447",
        calls: [
            {
                service: "activityTodoForStudents",
                routeParams: "{}",
                data: "{ material_id, has_prerequisite_quiz, student_ids, live_session_id: null }",
            },
        ],
    },
    student_open: {
        gesture: "« Activité » pour un élève : ses contrôles à venir, ses chapitres",
        legacy: "GenerateActivityModal.js:644-653, 762-798",
        calls: [
            {
                service: "getSchoolAssessments",
                routeParams: "{}",
                urlParams: "{ student_id, upcoming: true }",
            },
            {
                service: "getChaptersAsTutor",
                routeParams: "{ id: élève }",
                urlParams: "{ school_subject_id, class, page_size: 100 }",
            },
        ],
    },
    student_create: {
        gesture: "Un élève : « Créer l'activité » d'une carte",
        legacy: "GenerateActivityModal.js:1448-1455",
        calls: [
            {
                service: "createActivityAsTutor",
                routeParams: "{ id: élève }",
                data: "{ material_id, has_prerequisite_quiz }",
            },
        ],
    },
    file_create: {
        gesture: "Une pièce du chat : l'étape du fichier, « Créer l'activité », puis l'élève relu",
        legacy: "GenerateActivityModal.js:1480-1511, 1767-1782, 1914-1974, 1162-1165",
        calls: [
            {
                service: "analyzeMaterial",
                routeParams: "{}",
                data: "{ student_id, student_level, school_subject_id, message_id } — sans téléversement pour une pièce du chat",
            },
            {
                service: "createActivityAsTutor",
                routeParams: "{ id: élève }",
                data: "{ material_id, estimated_duration, has_prerequisite_quiz, school_subject_id, chapter_id }",
            },
            { service: "getStudent", routeParams: "{ id: élève }" },
        ],
    },
    file_crop: {
        gesture:
            "Au web, une photo : « Recadrer l'image » (la zone part de la photo entière et suit le geste), « Valider » joint la zone découpée, « Passer » la photo entière, la croix abandonne",
        legacy: "tutor/students/components/ImageCropModal.web.js (createCroppedImage, handleConfirm, handleSkip)",
        calls: [],
    },
    program_pick: {
        gesture: "Mode programme : le choix d'un chapitre lit ses programmes, la génération part sur son cadrage",
        legacy: "StudyProgramsFilterList.js:44 ; GenerateActivityModal.js:1039-1049",
        calls: [
            { service: "getStudyProgramsAsTutor", routeParams: "{}", urlParams: "{ student_ids: élève }" },
            {
                service: "generateMaterialsSkillsPart1",
                routeParams: "{ id: élève }",
                data: "{ class et school_subject_id du chapitre, chapter_id: chapitre du programme, … }",
            },
        ],
    },
    from_activity: {
        gesture: "« Générer... » d'une activité : les trois semblables de l'exo",
        legacy: "ParcoursChoiceModal.js:316-335 ; GenerateActivityModal.js:910-985",
        calls: [
            {
                service: "generateSimilarExercisesSameDifficulty",
                routeParams: "{}",
                data: "{ material_id, target_level, duration, student_ids }",
            },
            { service: "generateSimilarExercisesOtherConcepts", routeParams: "{}", data: "idem" },
            { service: "generateSimilarExercisesSameDifficultyDiversified", routeParams: "{}", data: "idem" },
        ],
    },
    from_activity_unreliable: {
        gesture:
            "« Générer... » d'un exo au chapitre peu sûr : ses chapitres listés, le choix enregistré par la correction du chapitre, puis les semblables",
        legacy: "ParcoursChoiceModal.js (useSourceChapterFix)",
        calls: [
            {
                service: "getChaptersAsTutor",
                routeParams: "{ id: élève }",
                urlParams: "{ school_subject_id: matière du support, class: classe de l'élève, page_size: 100 }",
            },
            { service: "updateMaterialChapter", routeParams: "{ id: support }", data: "{ chapter_id }" },
        ],
    },
    from_activity_no_chapter: {
        gesture:
            "« Générer... » d'un exo au chapitre peu sûr, sa matière sans chapitre dans la classe : « Générer sans chapitre »",
        legacy: "ParcoursChoiceModal.js (useSourceChapterFix)",
        calls: [
            { service: "updateMaterialChapter", routeParams: "{ id: support }", data: "{ chapter_id: null, class }" },
            { service: "generateSimilarExercisesSameDifficulty", routeParams: "{}", data: "{ material_id, … }" },
        ],
    },
    quiz: {
        gesture: "Quiz de prérequis : allumé d'office (Entraînement), envoyé avec la carte ; un refus se dit sur elle",
        legacy: "MaterialResultsCards.js:231-257, 580-607 ; GenerateActivityModal.js:126-128, 1411-1426",
        calls: [
            {
                service: "createActivityAsTutor",
                routeParams: "{ id: élève }",
                data: "{ material_id, has_prerequisite_quiz: true }",
            },
        ],
    },
} as const

export type TutorGenerateActivity = {
    /** Ouvre le parcours pour une cible. Absent : pas de bouton « Activité ». */
    open: OpenGeneration | undefined
    overlay: React.ReactNode
}

// ── L'état du crochet : pur, joué dans un test ───────────────────────────────

/** La séance ouverte : pour qui, et d'où. */
export type TutorGenerateSession =
    | { kind: "students"; selectedIds: TutorStudentId[]; pickerOpen: boolean }
    | { kind: "student"; studentId: TutorStudentId }
    | { kind: "file"; studentId: TutorStudentId; file: UIActivityFile }
    | { kind: "fromActivity"; studentId: TutorStudentId; source: TutorGenerateSource | null }

export type TutorGenerateState = {
    session: TutorGenerateSession | null
    /** Monte à chaque ouverture : le parcours partagé s'ouvre au rendu qui suit, sur la bonne cible. */
    openToken: number
    program: { enabled: boolean; chapter: UITutorProgramChapter | null; sheetOpen: boolean }
}

export const initialTutorGenerateState: TutorGenerateState = {
    session: null,
    openToken: 0,
    program: { enabled: false, chapter: null, sheetOpen: false },
}

export type TutorGenerateAction =
    | { type: "OPEN"; session: TutorGenerateSession; programEnabled: boolean }
    | { type: "TOGGLE_STUDENT"; id: TutorStudentId }
    | { type: "SELECT_ALL"; ids: TutorStudentId[] }
    | { type: "SET_PICKER"; open: boolean }
    | { type: "SET_PROGRAM"; enabled: boolean }
    | { type: "OPEN_PROGRAM_SHEET" }
    | { type: "PICK_PROGRAM_CHAPTER"; chapter: UITutorProgramChapter }
    | { type: "CLOSE_PROGRAM_SHEET" }
    | { type: "CLOSE" }

export function tutorGenerateReducer(state: TutorGenerateState, action: TutorGenerateAction): TutorGenerateState {
    switch (action.type) {
        case "OPEN":
            return {
                session: action.session,
                openToken: state.openToken + 1,
                program: { enabled: action.programEnabled, chapter: null, sheetOpen: false },
            }
        // L'ordre des élèves envoyés est celui de la sélection (`[...prev, student.id]`).
        case "TOGGLE_STUDENT": {
            if (state.session?.kind !== "students") return state
            const { selectedIds } = state.session
            const selected = selectedIds.some((id) => String(id) === String(action.id))
            return {
                ...state,
                session: {
                    ...state.session,
                    selectedIds: selected
                        ? selectedIds.filter((id) => String(id) !== String(action.id))
                        : [...selectedIds, action.id],
                },
            }
        }
        case "SELECT_ALL":
            return state.session?.kind === "students"
                ? { ...state, session: { ...state.session, selectedIds: action.ids } }
                : state
        case "SET_PICKER":
            return state.session?.kind === "students"
                ? { ...state, session: { ...state.session, pickerOpen: action.open } }
                : state
        // Entrer dans le mode programme efface le chapitre choisi à la main ; en sortir, celui du programme
        // (`handleSetUseStudyProgram`). L'écran rejoue le cadrage sur le brouillon.
        case "SET_PROGRAM":
            if (action.enabled === state.program.enabled) return state
            return { ...state, program: { enabled: action.enabled, chapter: null, sheetOpen: false } }
        case "OPEN_PROGRAM_SHEET":
            return { ...state, program: { ...state.program, sheetOpen: true } }
        case "PICK_PROGRAM_CHAPTER":
            return { ...state, program: { ...state.program, chapter: action.chapter, sheetOpen: false } }
        case "CLOSE_PROGRAM_SHEET":
            return { ...state, program: { ...state.program, sheetOpen: false } }
        case "CLOSE":
            return { ...initialTutorGenerateState, openToken: state.openToken }
        default:
            return state
    }
}

/** L'élève de la séance : le sien, ou le premier choisi d'une activité commune (`resolvedStudent`). */
export function representativeStudentId(
    session: TutorGenerateSession | null,
    students: ReadonlyArray<ApiGenerateStudent | null | undefined>
): TutorStudentId | null {
    if (session == null) return null
    if (session.kind !== "students") return session.studentId
    const wanted = new Set(session.selectedIds.map(String))
    return students.find((student) => student?.id != null && wanted.has(String(student.id)))?.id ?? null
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type State = any

/** Ses activités telles que `getStudent` les sert : la dernière finie donne la matière d'ouverture. */
function studentActivities(student: ApiGenerateStudent | null): Parameters<typeof lastFinishedActivitySubjectId>[0] {
    return (student?.activities ?? null) as Parameters<typeof lastFinishedActivitySubjectId>[0]
}

export function useTutorGenerateActivity(): TutorGenerateActivity {
    const dispatch = useDispatch()
    const [state, dispatchUi] = useReducer(tutorGenerateReducer, initialTutorGenerateState)
    const { session, program } = state

    const accountStudents = useSelector((s: State) => selectAccountStudents(s) as ApiGenerateStudent[])
    const quizAvailable = useSelector((s: State) => prerequisiteQuizAvailable(selectAccount(s)))
    const forStudents = session?.kind === "students"
    const selectedIds = session?.kind === "students" ? session.selectedIds : null
    const studentId = representativeStudentId(session, accountStudents)
    const single = session != null && !forStudents ? studentId : null
    const student = useSelector((s: State) =>
        studentId != null ? selectDisplayedStudent(s, studentId) : null
    ) as ApiGenerateStudent | null

    // ── La création qui part, pour lire sa réponse : un fichier (relu), ou une carte (son quiz) ──
    const creatingRef = useRef<{ kind: "file" } | { kind: "card"; id: string | number; quiz: boolean } | null>(null)
    const [quizErrors, setQuizErrors] = useState<Record<string, boolean>>({})

    const onCreatedActivity = useCallback(() => {
        const pending = creatingRef.current
        creatingRef.current = null
        // `GenerateActivityModal.js` : « Refresh student data to update chat message display » — après le
        // fichier, pour un élève seulement.
        if (pending?.kind === "file" && single != null) {
            dispatch(apiRequest(studentsService.getStudent, { id: single }) as never)
        }
    }, [dispatch, single])

    const commonClass = useSelector((s: State) =>
        selectedIds != null ? commonTargetClass(s, null, selectedIds) : null
    )

    // Au web, une photo choisie passe d'abord par le recadrage, qui la joint ensuite par le parcours (`attachFile`,
    // qui ne la retient pas une seconde fois) ; un PDF, et tout au téléphone, est joint aussitôt.
    const attachRef = useRef<(file: UIActivityFile) => void>(() => {})
    const crop = useTutorImageCrop((file) => attachRef.current(file))
    const flow = useGenerateActivityFlow({
        studentId,
        targetStudentIds: selectedIds ?? undefined,
        liveSessionId: null,
        quizAvailable,
        onCreatedActivity,
        holdFile: crop.hold,
    })
    attachRef.current = flow.attachFile
    const base = flow.generate

    // Le cadrage d'ouverture (la classe, puis la matière de la dernière activité finie), que la sortie du
    // mode programme rend aussi au tuteur (`defaultScope`).
    const openingScope = useCallback(() => {
        const lastSubjectId = forStudents ? null : lastFinishedActivitySubjectId(studentActivities(student))
        return {
            className: forStudents ? commonClass : (student?.profile?.class ?? null),
            subjectId: lastSubjectId != null ? String(lastSubjectId) : DEFAULT_SCHOOL_SUBJECT_ID,
        }
    }, [forStudents, commonClass, student])

    // ── L'ouverture : au rendu qui suit `OPEN`, quand le parcours partagé lit déjà la bonne cible ──
    const handledTokenRef = useRef(0)
    const lastProgramAvailabilityRef = useRef<boolean | null>(null)
    useEffect(() => {
        if (session == null || state.openToken === handledTokenRef.current) return
        handledTokenRef.current = state.openToken
        lastProgramAvailabilityRef.current = forStudents ? program.enabled : null
        creatingRef.current = null
        setQuizErrors({})
        crop.clear()
        flow.open()
        if (session.kind === "fromActivity") {
            // Le type de l'exo et la durée de la classe (`ParcoursChoiceModal` : `sourceLevel`,
            // `getDefaultMaterialDuration`) ; son support tient lieu de fichier lu.
            // Cadré sur la matière du support et la classe de l'élève : c'est là que son chapitre se choisit
            // quand il n'est pas sûr (`useSourceChapterFix`).
            const scope = openingScope()
            base.onDraftChange({
                ...emptyGenerationDraft,
                className: scope.className ?? session.source?.className ?? null,
                subjectId: session.source?.subjectId != null ? String(session.source.subjectId) : scope.subjectId,
                level: session.source?.level ?? "2",
                duration: defaultMaterialDuration(scope.className),
            })
            if (session.source != null)
                flow.seedMaterial({ id: session.source.materialId, title: session.source.title })
        }
        if (session.kind === "file" && !crop.hold(session.file)) flow.attachFile(session.file)
        // Le mode programme d'ouverture : le cadrage attend le chapitre du programme (classe et matière vides).
        if (program.enabled) {
            base.onDraftChange({
                ...emptyGenerationDraft,
                className: null,
                subjectId: null,
                duration: defaultMaterialDuration(openingScope().className),
            })
        }
        // Une fois par ouverture : le jeton suffit.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.openToken, session])

    const draftRef = useRef(base.draft)
    draftRef.current = base.draft
    const setProgram = useCallback(
        (enabled: boolean) => {
            if (enabled === program.enabled) return
            dispatchUi({ type: "SET_PROGRAM", enabled })
            // Le chapitre du programme vide, le cadrage l'est aussi : la zone de dépôt reste fermée.
            const scope = enabled ? { className: null, subjectId: null } : openingScope()
            base.onDraftChange({ ...draftRef.current, ...scope, chapterId: null, skillPartPositions: [] })
        },
        [program.enabled, openingScope, base]
    )

    // Une activité commune suit la disponibilité du programme parmi les élèves choisis, à la TRANSITION
    // seulement : une sortie à la main n'est pas contredite tant que la sélection ne la change pas.
    const selectionHasProgram = selectedIds != null && anyHasStudyProgram(accountStudents, selectedIds)
    useEffect(() => {
        if (!forStudents || handledTokenRef.current !== state.openToken) return
        if (lastProgramAvailabilityRef.current === selectionHasProgram) return
        lastProgramAvailabilityRef.current = selectionHasProgram
        setProgram(selectionHasProgram)
    }, [forStudents, selectionHasProgram, setProgram, state.openToken])

    // ── Les programmes de l'élève, pour le choix d'un chapitre (`StudyProgramsFilterList`) : la boîte du
    // lot 3, le programme du chapitre retenu montré d'abord ──
    const programPicker = useTutorProgramPicker(program.chapter?.id)
    const { load: loadPrograms, cancel: cancelPrograms, raw: programsRaw } = programPicker
    const openProgramSheet = useCallback(() => {
        const ids = selectedIds != null ? selectedIds.join(",") : single
        if (ids == null || ids === "") return
        dispatchUi({ type: "OPEN_PROGRAM_SHEET" })
        loadPrograms(ids)
    }, [selectedIds, single, loadPrograms])
    const pickProgramChapter = useCallback(
        (chapter: UITutorProgramChapter) => {
            dispatchUi({ type: "PICK_PROGRAM_CHAPTER", chapter })
            const scope = programChapterScope(programsRaw as ApiGenerateProgram[] | null, chapter.id)
            base.onDraftChange({
                ...draftRef.current,
                className: scope?.className ?? null,
                subjectId: scope?.subjectId ?? (chapter.subject?.id != null ? String(chapter.subject.id) : null),
                chapterId: chapter.id,
                skillPartPositions: [],
            })
        },
        [programsRaw, base]
    )

    // ── Les contrôles à venir d'un élève, à l'ouverture (`isOpen && student?.id && !forStudents`) ──
    useEffect(() => {
        if (!base.visible || single == null) return
        dispatch(
            apiRequest(
                schoolAssessmentsService.getSchoolAssessments,
                {},
                {},
                { student_id: single, upcoming: true }
            ) as never
        )
    }, [dispatch, base.visible, single])
    const assessments = useSelector(
        (s: State) =>
            (single != null ? s?.data?.schoolAssessments?.byStudentId?.[single] : undefined) as
                ApiStudentSchoolAssessment[] | undefined
    )
    const nextAssessment = useMemo(
        () => (single != null ? nextAssessmentForSubject(assessments, base.draft.subjectId) : null),
        [single, assessments, base.draft.subjectId]
    )

    // ── Instructions recommandées après une recherche sans issue (`MaterialGenerationForm.js`) ──
    const [instructionsRecommended, setInstructionsRecommended] = useState(false)
    const baseDraftChange = base.onDraftChange
    // Le chapitre choisi dans le formulaire d'un exo peu sûr part aussitôt par la correction du chapitre (`pick`
    // ne renvoie pas celui déjà enregistré).
    const fixRef = useRef<Pick<SourceChapterFix, "shown" | "pick">>({ shown: false, pick: () => {} })
    const onDraftChange = useCallback(
        (draft: UIActivityGenerationDraft) => {
            if (draft.chapterId != null) setInstructionsRecommended(false)
            if (fixRef.current.shown && draft.chapterId != null) fixRef.current.pick(draft.chapterId)
            baseDraftChange(draft)
        },
        [baseDraftChange]
    )
    const onDescribeInInstructions = useCallback(
        (searchText: string) => {
            setInstructionsRecommended(true)
            const next = describeInInstructions(draftRef.current, searchText)
            if (next !== draftRef.current) baseDraftChange(next)
        },
        [baseDraftChange]
    )

    // ── Le quiz de prérequis : allumé d'office sur une carte prête d'Entraînement ou de Révision ──
    const batchLevelRef = useRef<string | null>(null)
    const defaultedRef = useRef<Set<string>>(new Set())
    const results = base.results
    const { onToggleResultQuiz } = base
    useEffect(() => {
        if (!quizAvailable || results == null || results.length === 0) return
        if (results.every((card) => card.status === "pending")) {
            defaultedRef.current = new Set()
            return
        }
        for (const card of results) {
            const key = String(card.id)
            if (card.status !== "ready" || defaultedRef.current.has(key)) continue
            defaultedRef.current.add(key)
            onToggleResultQuiz?.(card.id, quizOnByDefault(batchLevelRef.current))
        }
    }, [quizAvailable, results, onToggleResultQuiz])

    // Une création refusée alors que le quiz était demandé : « Erreur lors de la génération du quiz »
    // sur sa carte (`quizErrorStates`), à la place du bandeau ; elle s'efface au prochain toucher du quiz.
    useEffect(() => {
        const pending = creatingRef.current
        if (base.error == null || pending?.kind !== "card") return
        creatingRef.current = null
        if (pending.quiz) setQuizErrors((previous) => ({ ...previous, [String(pending.id)]: true }))
    }, [base.error])

    const fromActivity = session?.kind === "fromActivity" ? session : null
    const fromSource = fromActivity?.source ?? null
    const fixClass = student?.profile?.class ?? fromSource?.className ?? null
    const fix = useSourceChapterFix({
        source:
            fromSource != null
                ? {
                      materialId: fromSource.materialId,
                      confidence: fromSource.confidence,
                      subjectId: fromSource.subjectId,
                  }
                : null,
        studentId: fromActivity?.studentId ?? null,
        className: fixClass,
        active: base.visible && fromActivity != null,
    })
    fixRef.current = fix
    // Un enregistrement refusé rend au champ le chapitre enregistré : le choix affiché est celui qui vaut.
    useEffect(() => {
        if (fix.error == null) return
        baseDraftChange({ ...draftRef.current, chapterId: fix.chapterId ?? null })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fix.error])
    // Sans exo d'une activité, la correction n'a pas de support : `fix` reste au repos.
    const sourceUnreliable = fix.unreliable
    const fixWithoutChapter = fix.status === "empty"
    // Le chapitre de l'exo se lit dans le formulaire tant que la correction se montre, sauf sans rien à choisir.
    const fixFields = fix.shown && (fix.status !== "empty" || fix.chapterId != null)

    const targetStudents = useMemo(() => toGenerateTargetStudents(accountStudents), [accountStudents])
    const selectedTargets = useMemo(() => {
        if (selectedIds == null) return undefined
        const byId = new Map(targetStudents.map((target) => [String(target.id), target]))
        return selectedIds
            .map((id) => byId.get(String(id)))
            .filter((target): target is (typeof targetStudents)[number] => target != null)
    }, [selectedIds, targetStudents])

    // Les props du parcours, construites seulement quand une séance est ouverte (l'overlay les lit alors).
    const buildGenerate = (open: TutorGenerateSession): TutorGenerateActivityFlowProps => {
        /** Une génération part : son niveau décide du quiz d'office de ses cartes (`quizOnByDefault`). */
        const similar = (level: string) => {
            batchLevelRef.current = level
            base.onGenerateSimilar?.(level)
        }
        const clearQuizError = (id: string | number) =>
            setQuizErrors((previous) => ({ ...previous, [String(id)]: false }))
        const close = () => {
            cancelPrograms()
            dispatchUi({ type: "CLOSE" })
            base.onClose()
        }
        return {
            ...base,
            visible: base.visible && crop.step == null && !program.sheetOpen,
            onClose: close,
            onDraftChange,
            // La liste du champ est celle de la correction : le choix, son attente, son échec et « Réessayer » se
            // lisent au même endroit que dans l'ancienne fenêtre.
            ...(fixFields && {
                catalogs: { ...base.catalogs, chapters: fix.sectionedChapters },
                chaptersLoading: fix.status === "loading",
                chaptersFailed: fix.status === "failed",
                onRetryChapters: fix.retry,
                chapterRequired: true,
            }),
            formFields:
                fromActivity != null
                    ? fixFields
                        ? TUTOR_GENERATE_FROM_ACTIVITY_FIX_FIELDS
                        : TUTOR_GENERATE_FROM_ACTIVITY_FIELDS
                    : program.enabled
                      ? TUTOR_GENERATE_PROGRAM_FIELDS
                      : TUTOR_GENERATE_FORM_FIELDS,
            allowFile: fromActivity == null,
            nextAssessment,
            chapterOrInstructions: !program.enabled,
            onDescribeInInstructions: program.enabled ? undefined : onDescribeInInstructions,
            instructionsRecommended: instructionsRecommended && base.draft.chapterId == null,
            // `isGenerationFormValid` : en programme, son chapitre ; sinon la classe, la matière, et un chapitre
            // OU cinq caractères d'instructions. L'exo d'une activité ne demande rien de plus.
            missingField:
                fromActivity != null
                    ? sourceUnreliable && !fixWithoutChapter
                        ? "chapter"
                        : null
                    : program.enabled
                      ? program.chapter != null
                          ? null
                          : "chapter"
                      : missingStudentGenerationField(base.draft),
            studyProgram:
                fromActivity == null && (forStudents ? selectionHasProgram : !!student?.has_study_program)
                    ? {
                          enabled: program.enabled,
                          onToggle: setProgram,
                          chapter:
                              program.chapter != null
                                  ? { name: program.chapter.name, parentName: program.chapter.parentName ?? undefined }
                                  : null,
                          onPickChapter: openProgramSheet,
                      }
                    : undefined,
            onGenerate: () => {
                // L'exo d'une activité : ses trois semblables, une fois son chapitre sûr — ou confirmé sans chapitre
                // quand sa matière n'en a aucun dans la classe.
                if (fromActivity != null) {
                    if (fromActivity.source == null) return
                    if (fixWithoutChapter) fix.confirmWithoutChapter(() => similar(draftRef.current.level))
                    else if (!sourceUnreliable) similar(base.draft.level)
                    return
                }
                batchLevelRef.current = base.draft.level
                base.onGenerate?.()
            },
            // Les semblables d'un exo ne se régénèrent pas (`canRegenerate`, `mode !== "similar"`).
            onRegenerate:
                fromActivity != null || base.onRegenerate == null
                    ? undefined
                    : () => {
                          batchLevelRef.current = base.draft.level
                          base.onRegenerate?.()
                      },
            onGenerateSimilar: similar,
            onCreateFromFile: () => {
                creatingRef.current = { kind: "file" }
                base.onCreateFromFile?.()
            },
            onCreateResult: (id: string | number) => {
                const card = base.results?.find((result) => result.id === id)
                creatingRef.current = { kind: "card", id, quiz: quizAvailable && card?.quizEnabled === true }
                clearQuizError(id)
                base.onCreateResult?.(id)
            },
            onToggleResultQuiz: base.onToggleResultQuiz
                ? (id: string | number, next: boolean) => {
                      clearQuizError(id)
                      base.onToggleResultQuiz?.(id, next)
                  }
                : undefined,
            results: (base.results ?? []).map((card) =>
                quizErrors[String(card.id)] ? { ...card, quizError: true } : card
            ),
            // Le refus d'une carte au quiz se dit sur la carte, pas en bandeau.
            error: fix.error != null ? fix.error : Object.values(quizErrors).some(Boolean) ? null : base.error,
            notice: sourceChapterFixNotice(fix.status, base.scopeLabel ?? null),
            submitLock: fix.saving ? { label: SOURCE_CHAPTER_FIX_TEXTS.saving } : null,
            submitLabel: fixWithoutChapter ? SOURCE_CHAPTER_FIX_TEXTS.withoutChapter : undefined,
            formTitle: fromActivity != null ? fromActivityTitle(fromActivity.source?.title) : undefined,
            ...(open.kind === "students" && {
                targetStudents: selectedTargets,
                onEditTargetStudents: () => dispatchUi({ type: "SET_PICKER", open: true }),
                picker: {
                    visible: base.visible && open.pickerOpen,
                    // Fermé sans aucun élève, il n'y a plus personne pour qui générer : le parcours se ferme (le
                    // legacy laissait une feuille sans élève, dont la création partait sans destinataire).
                    onClose: () =>
                        open.selectedIds.length === 0 ? close() : dispatchUi({ type: "SET_PICKER", open: false }),
                    students: targetStudents,
                    selectedIds: open.selectedIds,
                    onToggle: (id) => dispatchUi({ type: "TOGGLE_STUDENT", id }),
                    onSelectAll: () =>
                        dispatchUi({ type: "SELECT_ALL", ids: targetStudents.map((target) => target.id) }),
                    onConfirm: () => dispatchUi({ type: "SET_PICKER", open: false }),
                },
            }),
        }
    }

    // Les élèves se lisent au geste : `open` garde son identité d'un rendu à l'autre, dans chaque onglet.
    const store = useStore()
    const open = useCallback<OpenGeneration>(
        (target: TutorGenerateTarget) => {
            const students = selectAccountStudents(store.getState()) as ApiGenerateStudent[]
            const sessionFor = (): { session: TutorGenerateSession; programEnabled: boolean } | null => {
                switch (target.kind) {
                    case "students": {
                        const ids = students.map((item) => item?.id).filter((id): id is TutorStudentId => id != null)
                        if (ids.length === 0) return null
                        return {
                            session: { kind: "students", selectedIds: ids, pickerOpen: true },
                            programEnabled: anyHasStudyProgram(students, ids),
                        }
                    }
                    case "student":
                    case "file": {
                        const owner = students.find((item) => String(item?.id) === String(target.studentId))
                        const programEnabled = !!owner?.has_study_program
                        return target.kind === "student"
                            ? { session: { kind: "student", studentId: target.studentId }, programEnabled }
                            : {
                                  session: {
                                      kind: "file",
                                      studentId: target.studentId,
                                      file: {
                                          uri: target.file.uri,
                                          name: target.file.name ?? undefined,
                                          mimeType: target.file.mimeType ?? undefined,
                                          ...(target.file.messageId != null && { messageId: target.file.messageId }),
                                      },
                                  },
                                  programEnabled,
                              }
                    }
                    case "fromActivity": {
                        const owner = students.find((item) => String(item?.id) === String(target.studentId))
                        return {
                            session: {
                                kind: "fromActivity",
                                studentId: target.studentId,
                                source: generationSourceOf(owner, target.activityId, target.activity),
                            },
                            programEnabled: false,
                        }
                    }
                }
            }
            const next = sessionFor()
            if (next == null) return
            setInstructionsRecommended(false)
            dispatchUi({ type: "OPEN", ...next })
        },
        [store]
    )

    const overlay = (() => {
        if (session == null) return null
        if (crop.step != null) return <TutorImageCropStep {...crop.step} />
        if (program.sheetOpen) {
            return (
                <TutorRevisionProgramSheet
                    visible
                    onClose={() => {
                        cancelPrograms()
                        dispatchUi({ type: "CLOSE_PROGRAM_SHEET" })
                    }}
                    mode="pick"
                    programs={programPicker.programs}
                    selectedProgramId={programPicker.selectedProgramId}
                    onSelectProgram={programPicker.onSelectProgram}
                    onPickChapter={pickProgramChapter}
                    loading={programPicker.loading}
                    error={programPicker.error}
                    onRetry={programPicker.onRetry}
                />
            )
        }
        return <TutorGenerateActivityFlow {...buildGenerate(session)} />
    })()

    return { open, overlay }
}
